/** @format */

import axios from 'axios';
import { ElMessage as $message } from 'element-plus';
import { Storage } from '@power/utils';
import { getErrorMsg } from './error-msg';
import type { AxiosRequestConfig } from 'axios';
import type * as Request from './types';
import { ACCESS_TOKEN_KEY } from '@/enums/cacheEnum';
// import { useUserStore } from '@/store/modules/user';
import { useGlobalStoreWithOut } from '@/store/modules/global';

// 是否生产环境
// const IS_PROD = ['production', 'prod'].includes(import.meta.env.NODE_ENV);
/** 真实请求的路径前缀 */
// eslint-disable-next-line prefer-const
export let baseApiUrl = import.meta.env.VITE_APP_BASE_API;
/** mock请求路径前缀 */
const baseMockUrl = import.meta.env.VITE_APP_BASE_API_MOCK;

export const service = axios.create({
	// baseURL: baseApiUrl,
	timeout: 60 * 1000, //设置超时
});

service.interceptors.request.use(
	(config) => {
		return reqResolve(config);
	},
	(error) => {
		return reqReject(error);
	},
);

service.interceptors.response.use(
	(response) => {
		return resResolve(response);
	},
	(error) => {
		return resReject(error);
	},
);

export let reqResolve = (config) => {
	const token = Storage.get(ACCESS_TOKEN_KEY);
	if (token && config.headers) {
		// 请求头token信息，请根据实际情况进行修改
		config.headers['Authorization'] = `Bearer ${token}`;
	}
	return config;
};

const reqReject = (error) => {
	Promise.reject(error);
};

export let resResolve = (response) => {
	const res = response.data;

	if (response.config.responseType === 'blob' && res) {
		if (response.headers['content-disposition']) {
			res['contentDisposition'] = response.headers['content-disposition'];
		}
		return { data: res, error: false };
	}

	// if the custom code is not 200, it is judged as an error.
	if (res.code && res.code !== 200) {
		// Unauthorized
		if (res.code === 401) {
			useGlobalStoreWithOut().toLogin();
		}

		// Illegal token
		if (res.code === 11001 || res.code === 11002) {
			window.localStorage.clear();
			window.location.reload();
		}

		return { data: res, error: true };
	}

	return { data: res, error: false };
};

export let resReject = (error) => {
	const errMsg = getErrorMsg({ code: error.response.status });
	$message.error({
		dangerouslyUseHTMLString: true,
		message: errMsg,
	});
	error.message = errMsg;
	return Promise.reject(error);
};

/**
 *
 * @param method - request methods
 * @param url - request url
 * @param data - request data or params
 */
// eslint-disable-next-line prefer-const
export let request = async <T>(
	config: AxiosRequestConfig & {
		type?: AxiosRequestConfig['method'];
	},
	options: Request.RequestOptions = {},
): Promise<T> => {
	try {
		const { successMsg, errorMsg, isMock, isGetDataDirectly = true, isRoot } = options;

		// 如果当前是需要鉴权的接口 并且没有权限的话 则终止请求发起
		// if (permCode && !useUserStore().perms.includes(permCode)) {
		// 	const noPerm = '你没有访问该接口的权限，请联系管理员！';
		// 	$message.error(noPerm);
		// 	throw new Error(noPerm);
		// }

		const fullUrl = `${
			(isRoot ? '' : isMock ? baseMockUrl : window.pData?.env?.VITE_ENV_APP_BASE_API || baseApiUrl) + config.url
		}`;
		config.url = fullUrl.replace(/([^:])\/{2,}/g, '$1/');

		// 待后端一起处理时间戳
		// !config.params && (config.params = {});
		// config.params['_t'] = new Date().getTime();

		// if (IS_PROD) {
		//   // 保持api请求的协议与当前访问的站点协议一致
		//   config.url.replace(/^https?:/g, location.protocol);
		// }
		const method = config.method || config.type;

		const res: { data: any; error: boolean } = await service.request({ ...config, method });

		if (!res.error) {
			successMsg && $message.success(successMsg);
		} else {
			$message.error({
				dangerouslyUseHTMLString: true,
				message: errorMsg || getErrorMsg(res.data),
			});
			return Promise.reject(res.data);
		}

		return isGetDataDirectly ? res.data.data : res.data;
	} catch (error: any) {
		return Promise.reject(error);
	}
};

/**
 * 绝对路径请求。
 * 使用这个方法的时候需要注意域名后子目录适配，需要调用 getEnvUrlByUrl 方法。
 */
// eslint-disable-next-line prefer-const
export let requestRoot = <T>(config: AxiosRequestConfig, options: Request.RequestOptions = {}): Promise<T> => {
	return request<T>(config, { ...options, isRoot: true });
};
